import {
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTerminalInventoryAuditQuery } from "Api/Queries/TerminalInventory/useTerminalInventoryAuditQuery";
import { useTerminalStatusLabels } from "Components/Admin/Terminals/TerminalStatusChip";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { TerminalFinancing } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.Admin.Terminals;

type Props = {
  publicID: string;
};

export const TerminalAuditSection = ({ publicID }: Props) => {
  const { t } = useResource();
  const statusLabels = useTerminalStatusLabels();
  const {
    data: auditRecords,
    isLoading,
    isError,
  } = useTerminalInventoryAuditQuery(publicID);

  const fieldLabels: Record<string, string> = {
    SerialNumber: t(RESOURCES.Table.SerialNumber),
    HardwareModel: t(RESOURCES.Table.HardwareModel),
    Status: t(RESOURCES.Table.Status),
    HunterParty: t(RESOURCES.Table.Hunter),
    Financing: t(RESOURCES.Table.Financing),
    PurchasePrice: t(RESOURCES.Table.PurchasePrice),
    PurchasePriceCurrencyCode: t(RESOURCES.Form.Currency),
  };

  const formatAuditValue = (value?: string | null): string => {
    if (!value) {
      return "—";
    }
    if (value in statusLabels) {
      return statusLabels[value as keyof typeof statusLabels];
    }
    if (value === TerminalFinancing.Moone) {
      return t(RESOURCES.Financing.Moone);
    }
    if (value === TerminalFinancing.Hunter) {
      return t(RESOURCES.Financing.Hunter);
    }
    return value;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={24} />
        </Stack>
      );
    }

    if (isError || !auditRecords) {
      return <ErrorText>{t(RESOURCES.Messages.LoadError)}</ErrorText>;
    }

    if (auditRecords.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.Audit.Empty)}
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t(RESOURCES.Audit.When)}</TableCell>
            <TableCell>{t(RESOURCES.Audit.Field)}</TableCell>
            <TableCell>{t(RESOURCES.Audit.Change)}</TableCell>
            <TableCell>{t(RESOURCES.Audit.Who)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {auditRecords.map(
            ({ recordID, changedAt, field, oldValue, newValue, changedBy }) => (
              <TableRow key={recordID}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(changedAt, true)}
                </TableCell>
                <TableCell>{fieldLabels[field] ?? field}</TableCell>
                <TableCell>
                  {formatAuditValue(oldValue)} → {formatAuditValue(newValue)}
                </TableCell>
                <TableCell>{changedBy ?? "—"}</TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">{t(RESOURCES.Audit.Title)}</Typography>
      {renderContent()}
    </Stack>
  );
};
